import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { QueryClientProvider } from 'react-query';
import { Router, useLocation } from 'react-router-dom';
import { SkeletonTheme } from 'react-loading-skeleton';
import { I18nextProvider } from 'react-i18next';

import { CommonModal, CommonModalProps } from 'components/Modal/CommonModal';
import { RecoilBridgeInner } from 'components';
import { RabbykitProvider } from '@/components/RabbykitProvider';

import { i18nInstance } from './i18n';
import { queryClient } from './queryClient';
import { history } from './history';

export interface WrapModalProps<TResolved extends any = any, TRejected extends any = any> {
  hide(): void;
  onResolve(arg?: TResolved): void;
  onReject(arg?: TRejected): void;
}

export function wrapModalPromise<T extends object | undefined, R = unknown>(
  Component: (arg: T & WrapModalProps<R>) => JSX.Element | null,
  modalProps?: CommonModalProps & { wrapInCommonModal?: boolean },
) {
  return (props: T): Promise<R> => {
    const div = document.createElement('div');
    document.body.appendChild(div);
    const WrappedComponent = ({
      onResolve,
      onReject,
    }: Omit<WrapModalProps, 'hide'>): React.ReactElement<typeof CommonModal> => {
      const [open, setOpen] = useState(true);
      const location = useLocation();
      const [currentPath] = useState(location.pathname);
      const { hideModalWhenPathChange = true, wrapInCommonModal = true } = modalProps || {};

      const hide = useCallback(() => {
        setOpen(false);
        // in case long time no resolve promise cause memory leak
        // if you want resolve this promise, just call [[onResolve]] before hide
        // the promise state won't change after resolved
        onReject('hide');
      }, [setOpen, onReject]);

      useEffect(() => {
        if (currentPath !== location.pathname && hideModalWhenPathChange) {
          hide();
        }
      }, [currentPath, hide, hideModalWhenPathChange, location.pathname]);

      return wrapInCommonModal ? (
        <CommonModal on={open} onRequestClose={hide} {...modalProps}>
          <Component {...props} hide={hide} onReject={onReject} onResolve={onResolve} />
        </CommonModal>
      ) : (
        <Component {...props} hide={hide} onReject={onReject} onResolve={onResolve} />
      );
    };

    return new Promise((resolve, reject) => {
      const handleCancel = (arg: any) => {
        ReactDOM.unmountComponentAtNode(div);
        div.parentElement?.removeChild(div);
        reject(arg);
      };

      ReactDOM.render(
        <Router history={history}>
          <SkeletonTheme baseColor="var(--bg-light-color)" highlightColor="var(--bg-default-color)">
            <I18nextProvider i18n={i18nInstance}>
              <QueryClientProvider client={queryClient}>
                <RecoilBridgeInner>
                  <RabbykitProvider>
                    <WrappedComponent onResolve={resolve} onReject={handleCancel} />
                  </RabbykitProvider>
                </RecoilBridgeInner>
              </QueryClientProvider>
            </I18nextProvider>
          </SkeletonTheme>
        </Router>,
        div,
      );
    });
  };
}
